import type { Mesh, SceneControl } from '@anov/3d-core'
import { Box3, Color, Group, LoaderModule, MeshBasicMaterial, ShaderMaterial } from '@anov/3d-core'
import _ from 'lodash'
import { StrategyManage as BubbleStrategy } from '../Poi/particle/index'
import ObjectLayer from '../Base/ObjectLayer'
import type DynamicCircleMesh from '../Poi/particle/DynamicCircle'
import { SOURCE_BASE_PATH } from '../Utils/constant'
import type { Option } from './type'
import { ELevel } from './type'
import type { AnimationOption } from './KeyframeAnimation'
import { EMarkAnimationType, KeyframeAnimation } from './KeyframeAnimation'
import TanhaoShader from './shader/tanhao'

const DEFAULT_OPTION = {
  uri: '', // 统一资源标识符，既可以是code也是可以资源路径
  level: 0,
  animation: {
    type: [EMarkAnimationType.Bounce], // 动画类型："pulse"(脉冲)、"flash"(闪烁)、"bounce"(弹跳)，默认"pulse"
    duration: 1,
    enable: true,
  },
  bubble: {
    uri: '',
    visible: true,
    radius: 0.5,
    color: '',
  },
}

const COLORS = ['#538FFF', '#FFA753', '#FF6353']
const MODEL_URI = `${SOURCE_BASE_PATH}/element/mark.glb`
const load = new LoaderModule()

class Mark extends ObjectLayer {
  private _uri: string
  private _level: ELevel
  private _animation: Option['animation']
  private _bubble: Option['bubble']

  private _readyPromise: Promise<boolean>
  private _mesh: Group
  private _markMesh: Group
  private _bubbleMesh: DynamicCircleMesh
  // 动画相关参数
  private _animationController: KeyframeAnimation

  public get ready() {
    return this._readyPromise
  }

  public get uri(): string {
    return this._uri
  }

  public set uri(value: string) {
    this._uri = value
  }

  public get animation(): AnimationOption {
    return this._animation
  }

  public get level(): ELevel {
    return this._level
  }

  public set level(value: ELevel) {
    if (this._level === value)
      return
    this._level = Math.max(0, Math.min(value, 3))
    this.setMeshLevel()
    this.bubble.color = this.getColor()
  }

  public get bubble(): Option['bubble'] {
    return this._bubble
  }

  public set bubble(value: Option['bubble']) {
    this._bubble = value
  }

  constructor(option: Option, sceneControl: SceneControl) {
    super(sceneControl)

    const param = _.mergeWith(_.cloneDeep(DEFAULT_OPTION), option, this.arrayMergeCustomizer)
    const { uri, animation, level = ELevel.Low, bubble } = param
    this._uri = uri
    this._animation = animation
    this._level = Math.max(0, Math.min(level, 3))
    this._bubble = bubble
    if (!bubble.color)
      this._bubble.color = this.getColor()

    this._readyPromise = this.init()
  }

  // 动画设置
  private async init() {
    const group = new Group()
    this._markMesh = await this.loadModel(MODEL_URI) as Group
    group.add(this._markMesh)

    // 气泡
    const BubbleClass = await BubbleStrategy.dynamicCircle()
    this._bubbleMesh = new BubbleClass({ radius: 0.5, ...this.bubble }, this.sceneControl)
    group.add(this._bubbleMesh)

    this._mesh = group
    this.add(this._mesh)

    this.setMeshLevel()
    this.setModelPosition()

    this._animationController = new KeyframeAnimation(this._markMesh, this._animation)

    this._bubble = this.createProxy(this._bubble, this._bubbleMesh)
    this._animation = this.createProxy(this._animation, null, (key, value) => {
      this._animationController[key] = value
    })
    return true
  }

  private setModelPosition() {
    if (this.bubble.visible !== false) {
      const { height } = this.getBoxSize(this._markMesh)
      this._markMesh.position.setY(height * 0.5)
    }
    else { this._markMesh.position.setY(0) }
  }

  private loadModel(uri: string) {
    return new Promise((resolve, reject) => {
      load.loadGLTF({
        url: uri,
        autoBackward: false,
        isCache: true,
        draco: true,
        onLoad: (gltf) => {
          resolve(gltf.scene)
        },
        onError: () => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject(null)
        },
      })
    })
  }

  private setMeshLevel() {
    const { vertexShader, fragmentShader } = TanhaoShader
    const panelMaterial = new ShaderMaterial({
      uniforms: {
        color: { value: new Color(this.getColor()) },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      // 设置材质双面渲染，确保从正反两面都能正确显示
      side: 2,
      // 关闭深度测试
      depthTest: true,
      depthWrite: false,

    })
    const borderMaterial = new MeshBasicMaterial({ color: this.getColor() })

    this._markMesh.traverse((object) => {
      if (object.type === 'Mesh') {
        const mesh = object as Mesh
        if (mesh.name === 'Mesh') // TODO：等确定命名规范
          mesh.material = borderMaterial
        if (mesh.name === 'Mesh_1')
          mesh.material = panelMaterial
        // // 处理材质数组
        // if (Array.isArray(mesh.material)) {
        //   // 如果是多材质对象
        //   (mesh.material as Material[]).forEach((material) => {
        //     (material as MeshBasicMaterial).color.set(this.getColor())
        //   })
        // }
        // else {
        //   // 单一材质
        //   (mesh.material as MeshBasicMaterial).color.set(this.getColor())
        // }
      }
    })
  }

  private getColor() {
    return COLORS[this.level]
  }

  // 更新动画混合器
  public async update(delta: number) {
    await this.ready
    this._animationController?.update(delta)
  }

  // 播放动画
  public async play(type?: EMarkAnimationType) {
    await this.ready
    this._animationController.play(type)
  }

  // 播放动画
  public async stop(type?: EMarkAnimationType) {
    await this.ready
    this._animationController.stop(type)
  }

  private getBoxSize(mesh) {
    // mesh.updateWorldMatrix(true, true)
    const { max, min } = new Box3().setFromObject(mesh)
    return { width: max.x - min.x, height: max.y - min.y }
  }

  // 销毁资源
  public dispose() {
    if (this._animationController) {
      this._animationController.destroy()
      this._animationController = null
    }
  }
}

export { Mark, EMarkAnimationType }
